import {
  Document,
  Font,
  Page,
  StyleSheet,
  Text,
  View,
} from '@react-pdf/renderer';
import { useTranslation } from 'react-i18next';

import { RateBucketPdf } from '@/components/Share/RateBucketPdf';
import { RateStarPdf } from '@/components/Share/RateStarPdf';
import { ReportSectionItemPdf } from '@/components/Share/ReportSectionItemPdf';
import { type CategorizedQuestionAnswerDto } from '@/services/api';

import type { CoachShare } from './types';

type PdfContentProps = {
  result: CoachShare;
  averageScore: number;
};

export const PdfContent: React.FC<PdfContentProps> = ({
  result,
  averageScore,
}) => {
  const { t } = useTranslation(['coach', 'interview']);
  const firstName = result?.name ?? '';
  const lastName = result?.surname ?? '';

  return (
    <Document>
      <Page size="A4" style={styles.page} wrap={false}>
        <View style={styles.contentContainer}>
          <View style={styles.headerContainer}>
            <View style={styles.avatarContainer}>
              <View style={styles.avatarLogoContainer}>
                <Text style={styles.avatarName}>
                  {firstName[0]?.toAppLocaleUpperCase()}
                  {lastName[0]?.toAppLocaleUpperCase()}
                </Text>
              </View>
              <View style={styles.avatarTextContainer}>
                <Text style={styles.avatarName}>
                  {result?.name + ' ' + (result?.surname ?? '')}
                </Text>
                <Text style={styles.avatarEmail}>{result?.email}</Text>
              </View>
            </View>
            <Text style={styles.dateText}>
              {result?.createdAt && formatDate(new Date(result?.createdAt))}
            </Text>
            <View style={{ ...styles.circle, ...styles.circleTopLeft }} />
            <View style={{ ...styles.circle, ...styles.circleTopRight }} />
          </View>

          <View style={styles.flexColumnCenter}>
            <Text style={styles.sectionTitle}>
              {t('coach:result.share.Your')} {result?.title}{' '}
              {t('interview:title')}
            </Text>
            <Text style={styles.sectionSubtitle}>
              {t('coach:result.share.title')}
            </Text>
            <RateStarPdf percent={averageScore} />

            <Text style={styles.ratingText}>{averageScore}</Text>

            <Text style={styles.scoreComparisonText}>
              {t('coach:result.share.ScoreComparison1')}{' '}
              <Text style={styles.markText}>
                {t('coach:result.share.Impressive')}
              </Text>{' '}
              {t('coach:result.share.ScoreComparison2')}{' '}
              {result?.successPosition?.toPrecision(2)}%{' '}
              {t('coach:result.share.ScoreComparison3')}
            </Text>
          </View>

          <View style={styles.questionsContainer}>
            {!!result?.questions?.length &&
              result?.questions?.map((catQuestion, index) => (
                <RateBucketPdf
                  key={catQuestion.averageRating}
                  index={index}
                  title={catQuestion?.skill ?? ''}
                  percent={catQuestion?.averageRating ?? 0}
                />
              ))}
          </View>

          <View style={styles.reportContainer}>
            {(result?.questions as Array<CategorizedQuestionAnswerDto>)?.map(
              (question) => (
                <ReportSectionItemPdf
                  key={question.skill}
                  title={question.skill ?? ''}
                  point={question.averageRating ?? 0}
                  pluses={
                    question.questionAnswers &&
                    question?.questionAnswers[0]?.answer?.pluses
                  }
                />
              )
            )}
          </View>

          <View style={{ ...styles.circle, ...styles.circleBottomRight }} />
          <View style={{ ...styles.circle, ...styles.circleBottomLeft }} />
        </View>
      </Page>
    </Document>
  );
};

const formatDate = (date: Date) => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${day}.${month}.${year} - ${hours}:${minutes}`;
};

Font.register({ family: 'Inter', src: '/fonts/Inter_regular.ttf' });

const styles = StyleSheet.create({
  page: {
    padding: 16,
    backgroundColor: '#F1F2F2',
    fontFamily: 'Inter',
  },
  contentContainer: {
    width: '100%',
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    paddingBottom: 16,
    alignItems: 'center',
    border: '1px solid #F1F2F2',
    position: 'relative',
    fontSize: 14,
  },
  circle: {
    width: 28,
    height: 28,
    position: 'absolute',
    backgroundColor: '#F1F2F2',
    borderRadius: '50%',
    zIndex: 99,
  },
  circleBottomRight: {
    bottom: -10,
    right: -10,
  },
  circleBottomLeft: {
    bottom: -10,
    left: -10,
  },
  circleTopLeft: {
    top: -10,
    left: -10,
  },
  circleTopRight: {
    top: -10,
    right: -10,
  },
  headerContainer: {
    height: 100,
    backgroundColor: '#FAFBFB',
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
  },
  avatarLogoContainer: {
    padding: 10,
    border: '1px solid #F1F2F2',
    borderRadius: '100%',
    width: '48px',
    height: '48px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  avatarTextContainer: {
    marginLeft: 12,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
  },
  avatarName: {
    fontWeight: 'bold',
    color: '#2F3236',
    fontSize: 12,
    textAlign: 'left',
    marginBottom: 2,
  },
  avatarEmail: {
    color: '#6F7881',
    fontSize: 12,
  },
  dateText: {
    fontSize: 10,
    color: '#6F7881',
  },
  flexColumnCenter: {
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 25,
  },
  sectionTitle: {
    fontSize: 12,
    textAlign: 'center',
    color: '#6F7881',
  },
  sectionSubtitle: {
    paddingHorizontal: 16,
    textAlign: 'center',
    fontWeight: 'bold',
    fontSize: 18,
    fontFamily: 'Inter',
  },
  ratingContainer: {
    height: 18,
    width: 18,
    alignItems: 'center',
    border: '1px solid #F1F2F2',
  },
  ratingText: {
    fontWeight: 'bold',
    fontSize: 24,
    marginTop: -15,
  },
  scoreComparisonText: {
    fontSize: 12,
    marginTop: 16,
    textAlign: 'center',
    paddingHorizontal: 16,
    fontWeight: 300,
  },
  markText: {
    color: '#5B1EA8',
    fontWeight: 'bold',
  },
  questionsContainer: {
    width: '85%',
    position: 'relative',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginVertical: 16,
  },
  reportContainer: {
    justifyContent: 'center',
    paddingHorizontal: 24,
    width: '100%',
  },
  startCenter: {
    width: 72,
    height: 72,
  },
});
